import { z } from 'zod';
export const uuidSchema = z.string().uuid();
export const isoDateSchema = z.string().datetime({ offset: true });
export const bottleStatusSchema = z.enum(['draft', 'hidden', 'found', 'missing', 'retired']);
export const movementTypeSchema = z.enum([
    'initial_hide',
    'found',
    'rehidden',
    'admin_move',
    'reported_missing',
    'retired',
]);
export const claimStatusSchema = z.enum(['active', 'used', 'revoked', 'expired']);
export const moderationStatusSchema = z.enum(['unreviewed', 'safe', 'review', 'inappropriate']);
export const reportTypeSchema = z.enum([
    'quote_mismatch',
    'bottle_damaged',
    'bottle_missing',
    'inappropriate_message',
    'unsafe_location',
    'other',
]);
export const executionModeSchema = z.enum(['server', 'browser']);
export const aiProviderNameSchema = z.enum(['ollama', 'openai-compatible']);
export const bottleAccessModeSchema = z.enum(['active', 'read_only', 'private']);
export const publicAreaSchema = z.string().trim().min(2).max(160);
export const locationInputSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('device'),
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        accuracy: z.number().nonnegative().max(100_000).optional(),
        publicArea: publicAreaSchema.optional(),
    }),
    z.object({
        source: z.literal('manual'),
        description: z.string().trim().min(2).max(500),
        publicArea: publicAreaSchema.optional(),
    }),
]);
export const encryptedPayloadV1Schema = z.object({
    version: z.literal(1),
    algorithm: z.literal('aes-256-gcm'),
    keyVersion: z.number().int().positive(),
    iv: z.string().min(1),
    tag: z.string().min(1),
    ciphertext: z.string().min(1),
});
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
        requestId: z.string(),
    }),
});
export const publicMovementSchema = z.object({
    id: uuidSchema,
    type: movementTypeSchema,
    occurredAt: isoDateSchema,
    publicLocation: z.string().nullable(),
});
export const publicCommentSchema = z.object({
    id: uuidSchema,
    message: z.string(),
    publicLocation: z.string().nullable(),
    createdAt: isoDateSchema,
});
export const publicBottleSchema = z.object({
    id: uuidSchema,
    availability: z.enum(['active', 'read_only']),
    name: z.string(),
    status: bottleStatusSchema,
    quote: z.object({
        id: uuidSchema,
        name: z.string(),
        text: z.string(),
    }),
    hiddenCount: z.number().int().nonnegative(),
    journeyStartedAt: isoDateSchema.nullable(),
    lastMovementAt: isoDateSchema.nullable(),
    quoteBottleCount: z.number().int().nonnegative(),
    journey: z.array(publicMovementSchema),
    comments: z.array(publicCommentSchema),
});
export const unavailableBottleSchema = z.object({
    id: uuidSchema,
    availability: z.literal('unavailable'),
});
export const publicBottleResponseSchema = z.union([publicBottleSchema, unavailableBottleSchema]);
export const foundResponseSchema = z.object({
    claimId: uuidSchema,
    handoffVersion: z.number().int().positive(),
    token: z.string().min(32),
    rehideUrl: z.string(),
});
export const spamGuardSchema = z.object({
    website: z.string().max(0).optional(),
    formStartedAt: z.number().int().positive().optional(),
});
export const commentCreateSchema = spamGuardSchema.extend({
    message: z.string().trim().min(1).max(2_000),
    location: locationInputSchema.optional(),
});
export const rehideSchema = spamGuardSchema.extend({
    occurredAt: z.string().datetime({ offset: true }).optional(),
    location: locationInputSchema,
    responsibleHidingConfirmed: z.literal(true),
});
export const reportCreateSchema = spamGuardSchema.extend({
    type: reportTypeSchema,
    commentId: uuidSchema.optional(),
    message: z.string().trim().max(2_000).optional(),
    location: locationInputSchema.optional(),
});
export const loginSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(12).max(256),
});
export const quoteCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    text: z.string().trim().min(1).max(2_000),
    description: z.string().trim().max(4_000).nullable().optional(),
    isActive: z.boolean().default(true),
});
export const quoteUpdateSchema = quoteCreateSchema.partial().extend({
    physicalQuoteReplaced: z.boolean().optional(),
});
export const bottleCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    quoteId: uuidSchema,
    status: z.enum(['draft', 'hidden']).default('draft'),
    location: locationInputSchema.optional(),
});
export const bottleUpdateSchema = z.object({
    name: z.string().trim().min(1).max(160).optional(),
    quoteId: uuidSchema.optional(),
    status: bottleStatusSchema.optional(),
    accessMode: bottleAccessModeSchema.optional(),
    location: locationInputSchema.optional(),
    physicalQuoteReplaced: z.boolean().optional(),
});
export const adminBottlePrintItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    status: bottleStatusSchema,
    accessMode: bottleAccessModeSchema,
    quoteName: z.string(),
    publicUrl: z.string().url(),
    qrUrl: z.string().min(1),
});
export const adminBottlePrintResponseSchema = z.object({
    items: z.array(adminBottlePrintItemSchema).max(500),
    total: z.number().int().nonnegative().max(500),
});
export const moderationInputSchema = z.object({
    id: uuidSchema,
    message: z.string(),
});
export const moderationClassifications = ['safe', 'review', 'inappropriate'];
export const moderationCategories = [
    'safe',
    'spam',
    'harassment',
    'hate',
    'sexual_content',
    'violence',
    'personal_information',
    'illegal_content',
    'other',
];
export const moderationResultSchema = z.object({
    commentId: uuidSchema,
    classification: z.enum(moderationClassifications),
    categories: z.array(z.enum(moderationCategories)),
    reason: z.string().min(1).max(2_000),
    confidence: z.number().min(0).max(1).optional(),
});
const rawModerationResultSchema = z.object({
    commentId: uuidSchema,
    classification: z.string().trim().min(1).max(100),
    categories: z.array(z.string().max(100)).max(50),
    reason: z.string().min(1).max(2_000),
    confidence: z.number().min(0).max(1).optional(),
});
const categoryAliases = {
    none: 'safe',
    benign: 'safe',
    appropriate: 'safe',
    advertising: 'spam',
    promotion: 'spam',
    abuse: 'harassment',
    bullying: 'harassment',
    toxicity: 'harassment',
    racism: 'hate',
    discrimination: 'hate',
    sexual: 'sexual_content',
    adult: 'sexual_content',
    nsfw: 'sexual_content',
    violent: 'violence',
    pii: 'personal_information',
    privacy: 'personal_information',
    doxxing: 'personal_information',
    personal_info: 'personal_information',
    illegal: 'illegal_content',
    illicit: 'illegal_content',
    crime: 'illegal_content',
};
const classificationAliases = {
    benign: 'safe',
    appropriate: 'safe',
    ok: 'safe',
    flagged: 'review',
    uncertain: 'review',
    needs_review: 'review',
    unsafe: 'inappropriate',
    disallowed: 'inappropriate',
};
function moderationToken(value) {
    return value
        .normalize('NFKC')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
export function normalizeModerationResult(value) {
    const raw = rawModerationResultSchema.parse(value);
    const classificationToken = moderationToken(raw.classification);
    const classification = moderationClassifications.includes(classificationToken)
        ? classificationToken
        : (classificationAliases[classificationToken] ?? 'review');
    const allowedCategories = new Set(moderationCategories);
    const categories = [
        ...new Set(raw.categories.map((category) => {
            const token = moderationToken(category);
            if (allowedCategories.has(token)) {
                return token;
            }
            return categoryAliases[token] ?? 'other';
        })),
    ];
    if (!categories.length)
        categories.push(classification === 'safe' ? 'safe' : 'other');
    return moderationResultSchema.parse({ ...raw, classification, categories });
}
export function normalizeModerationResults(value) {
    return z.array(z.unknown()).max(1_000).parse(value).map(normalizeModerationResult);
}
export const moderationSystemPrompt = `You moderate anonymous messages for a family-friendly public art project.
Return JSON only in an object with a results array containing exactly one result per supplied comment.
Use each supplied comment ID exactly once. Allowed classifications: safe, review, inappropriate.
Allowed categories: safe, spam, harassment, hate, sexual_content, violence, personal_information,
illegal_content, other. Every result must contain commentId, classification, categories, reason, and
optional confidence. Valid example: {"results":[{"commentId":"019c72ea-5c10-7e82-aee6-0ac09c793e58","classification":"review","categories":["personal_information"],"reason":"May reveal personal information.","confidence":0.8}]}
Never invent comment IDs or category values.`;
export const moderationRunRequestSchema = z.object({
    dateFrom: z.string().datetime({ offset: true }),
    dateTo: z.string().datetime({ offset: true }),
    provider: aiProviderNameSchema,
    model: z.string().min(1).max(300),
    quoteId: uuidSchema.optional(),
    bottleId: uuidSchema.optional(),
});
export const browserModerationResultsSchema = z.object({
    results: z.array(moderationResultSchema).max(1_000),
});
export const aiProviderConfigSchema = z.object({
    name: z.string().trim().min(1).max(120),
    provider: aiProviderNameSchema,
    baseUrl: z.string().url(),
    model: z.string().trim().max(300).nullable().optional(),
    apiKey: z.string().max(2_000).nullable().optional(),
    isActive: z.boolean().default(true),
});
export const responsibleHidingText = 'Please hide responsibly. The aim of this project is to spread positive messages, not create litter or harm nature. Do not place bottles in waterways, protected environments, roads, dangerous locations or private property without permission. Choose somewhere safe where another person has a reasonable chance of discovering it.';
//# sourceMappingURL=index.js.map